"use client";

/**
 * ResumeGeneratorDisplay - Generated resume display
 */

import { useState } from "react";
import { Card, Tag, Button } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import { displayValue } from "../utils";
import { Description, Download, Work, School, Code } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolDivider,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface ResumeOutput {
  success?: boolean;
  data?: {
    personal?: {
      name?: string;
      email?: string;
      phone?: string;
      location?: string;
    };
    summary?: string;
    experience?: Array<{
      company: string;
      position: string;
      period?: string;
      description?: string;
      highlights?: string[];
    }>;
    education?: Array<{
      institution: string;
      degree: string;
      year?: string;
    }>;
    skills?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
  };
  downloadUrl?: string;
  error?: string;
}

export function ResumeGeneratorDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as ResumeOutput | undefined;
  const resume = result?.data;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title="Generated Resume"
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Description style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Generating resume..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && resume && (
          <Card
            variant="flat"
            padding="md"
            style={{
              margin: spacing[4],
              backgroundColor: primitives.neutral[50],
              borderRadius: radius.lg,
            }}
          >
            {/* Header */}
            {resume.personal && (
              <div style={{ textAlign: "center", marginBottom: spacing[5] }}>
                <span
                  style={{
                    ...textStyles.h2,
                    fontWeight: 700,
                    display: "block",
                  }}
                >
                  {resume.personal.name}
                </span>
                <div style={{ display: "flex", gap: spacing[4], justifyContent: "center", marginTop: spacing[2] }}>
                  {resume.personal.email && (
                    <span style={{ ...textStyles.body2, color: colors.text.muted }}>
                      {resume.personal.email}
                    </span>
                  )}
                  {resume.personal.phone && (
                    <span style={{ ...textStyles.body2, color: colors.text.muted }}>
                      {resume.personal.phone}
                    </span>
                  )}
                  {resume.personal.location && (
                    <span style={{ ...textStyles.body2, color: colors.text.muted }}>
                      {resume.personal.location}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Summary */}
            {resume.summary && (
              <>
                <ToolDivider spaced />
                <span style={{ ...textStyles.body2, display: "block" }}>{resume.summary}</span>
              </>
            )}

            {/* Experience */}
            {resume.experience && resume.experience.length > 0 && (
              <>
                <ToolDivider spaced />
                <ToolSectionHeader icon={<Work />} label="Experience" />
                {resume.experience.map((exp, i) => (
                  <div key={i} style={{ marginBottom: spacing[4] }}>
                    <span style={{ ...textStyles.body2, fontWeight: 600, display: "block" }}>
                      {exp.position}
                    </span>
                    <span style={{ ...textStyles.body2, color: colors.text.muted, display: "block" }}>
                      {exp.company} {exp.period && `| ${exp.period}`}
                    </span>
                    {exp.description && (
                      <span style={{ ...textStyles.body2, display: "block", marginTop: spacing[1] }}>
                        {exp.description}
                      </span>
                    )}
                    {exp.highlights && exp.highlights.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", marginTop: spacing[1] }}>
                        {exp.highlights.map((h, j) => (
                          <div key={j} style={{ paddingTop: spacing[1], paddingBottom: 0 }}>
                            <span style={textStyles.body2}>
                              {`\u2022 ${h}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Education */}
            {resume.education && resume.education.length > 0 && (
              <>
                <ToolDivider spaced />
                <ToolSectionHeader icon={<School />} label="Education" />
                {resume.education.map((edu, i) => (
                  <div key={i} style={{ marginBottom: spacing[2] }}>
                    <span style={{ ...textStyles.body2, fontWeight: 600, display: "block" }}>
                      {edu.degree}
                    </span>
                    <span style={{ ...textStyles.body2, color: colors.text.muted, display: "block" }}>
                      {edu.institution} {edu.year && `(${edu.year})`}
                    </span>
                  </div>
                ))}
              </>
            )}

            {/* Skills */}
            {resume.skills && resume.skills.length > 0 && (
              <>
                <ToolDivider spaced />
                <ToolSectionHeader icon={<Code />} label="Skills" />
                <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                  {resume.skills.map((skill, i) => (
                    <Tag key={i} variant="default" size="sm">
                      {displayValue(skill)}
                    </Tag>
                  ))}
                </div>
              </>
            )}

            {/* Download button */}
            {result?.downloadUrl && (
              <div style={{ marginTop: spacing[5], textAlign: "center" }}>
                <a href={result.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <Button
                    variant="primary"
                    startIcon={<Download />}
                  >
                    Download PDF
                  </Button>
                </a>
              </div>
            )}
          </Card>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default ResumeGeneratorDisplay;
